import { useParams, useNavigate } from "react-router-dom";
import useAxios from "../../../hooks/useAxios";
import { useContext, useEffect, useState } from "react";
import { CircularProgress, Typography, Box, Button, Chip, Tooltip } from "@mui/material";
import QuestionCard from "../../../components/QuizComponents/Cards/QuestionCard/QuestionCard";
import './QuizReviewPage.css';
import Header from "../../../components/Header/Header";
import UpdateQuizGradesModal from "../../../components/QuizComponents/Modals/UpdateQuizGradesModal/UpdateQuizGradesModal";
import { AuthContext } from "../../../context/AuthContext";

const navLinks = [
    
];

function QuizReviewPage() {
    const { id: quizId } = useParams();
    const navigate = useNavigate();
    const axiosInstance = useAxios();
    const { status } = useContext(AuthContext);
    const [questions, setQuestions] = useState([]);
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updateGradesModalOpen, setUpdateGradesModalOpen] = useState(false);
    const [hintToggleLoading, setHintToggleLoading] = useState(false);
    const [hintToggleError, setHintToggleError] = useState(null);
    const [browserLockToggleLoading, setBrowserLockToggleLoading] = useState(false);
    const [browserLockToggleError, setBrowserLockToggleError] = useState(null);
    const [terminateSessionsLoading, setTerminateSessionsLoading] = useState(false);


    const fetchQuizData = async () => {
        try {
            const response = await axiosInstance.get(`/quizzes/owner`);
            if (response.data.success) {
                const currentQuiz = response.data.quizzes.find(q => q.id == quizId);
                if (currentQuiz) {
                    setQuiz(currentQuiz);
                }
            }
        } catch (err) {
            console.error('Failed to fetch quiz data:', err);
        }
    };

    const fetchQuestions = async () => {
        try {
            const response = await axiosInstance.get(`/quizzes/${quizId}/questions`);
            if (response.data.success) {
                setQuestions(response.data.questions);
            } else {
                setError(response.data.message || "Failed to fetch questions.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const handleHintToggle = async () => {
        if (!quiz) return;
        
        setHintToggleLoading(true);
        setHintToggleError(null);
        try {
            const response = await axiosInstance.patch(`/quizzes/${quizId}/settings`, {
                hints_enabled: !quiz.hints_enabled
            });
            
            if (response.data.success) {
                setQuiz(prev => ({
                    ...prev,
                    hints_enabled: !prev.hints_enabled
                }));
                setHintToggleError(null); // Clear any previous errors
            }
        } catch (err) {
            console.error('Failed to update hint settings:', err);
            if (err.response?.data?.message) {
                setHintToggleError(err.response.data.message);
            } else {
                setHintToggleError('Failed to update hint settings. Please try again.');
            }
        } finally {
            setHintToggleLoading(false);
        }
    };

    const handleBrowserLockToggle = async () => {
        if (!quiz) return;
        
        setBrowserLockToggleLoading(true);
        setBrowserLockToggleError(null);
        
        try {
            const response = await axiosInstance.patch(`/quizzes/${quizId}/settings`, {
                browser_lock: !quiz.browser_lock
            });
            
            if (response.data.success) {
                // Update the quiz in the local state
                setQuiz(prev => ({ ...prev, browser_lock: !prev.browser_lock }));
                setBrowserLockToggleError(null);
            }
        } catch (err) {
            console.error('Failed to update browser lock settings:', err);
            setBrowserLockToggleError('Failed to update browser lock settings. Please try again.');
        } finally {
            setBrowserLockToggleLoading(false);
        }
    };

    const handleTerminateAllSessions = async () => {
        if (!quiz) return;
        
        setTerminateSessionsLoading(true);
        setHintToggleError(null);
        try {
            // First, terminate all active sessions
            const response = await axiosInstance.patch(`/quizzes/${quizId}/terminate-sessions`);
            
            if (response.data.success) {
                // Now try to toggle the hint mode again
                await handleHintToggle();
                setHintToggleError(null); // Clear any previous errors
            }
        } catch (err) {
            console.error('Failed to terminate sessions:', err);
            setHintToggleError('Failed to terminate sessions. Please try again.');
        } finally {
            setTerminateSessionsLoading(false);
        }
    };

    useEffect(() => {
        fetchQuizData();
        fetchQuestions();
    }, [quizId]);

    const handleUpdate = (id) => {
        navigate(`/update-question/${id}`);
    };

    const handleDelete = async (id) => {
        setLoading(true);
        setError(null);
    
        try {
            const response = await axiosInstance.delete(`/quizzes/${quizId}/questions/${id}`);
            if (response.data.success) {
                fetchQuestions(); // Re-fetch the updated list of questions
            } else {
                setError(response.data.message || "Failed to delete question.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "An unexpected error occurred while deleting the question.");
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = (refreshNeeded) => {
        setUpdateGradesModalOpen(false);
        if (refreshNeeded) {
          fetchQuestions(); // Re-fetch questions if needed
        }
      };

      const handleSubmit = () => {
        if (status === 'trial') {
            navigate('/owner-quizzes');
        } else {
            navigate('/dashboard');
        }
      };
      
    
    

    if (loading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Fetching Questions...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
                textAlign="center"
            >
                <Typography variant="h6" color="error" gutterBottom>
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Header navLinks={navLinks} isScrollEffect={false} />
            <div className="quiz-review-container">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px'}}>
                    <Typography className="quiz-review-title">
                        Assessment's Questions :
                    </Typography>
                    
                    {/* Hint Mode Badge */}
                    {quiz && (
                        <Tooltip 
                            title={quiz.hints_enabled 
                                ? "Students get three attempts for each question with hints on wrong answers"
                                : "Students get one attempt per question with no hints"
                            }
                            arrow
                        >
                            <Chip
                                label={`Hint Mode ${quiz.hints_enabled ? 'Enabled' : 'Disabled'}`}
                                color={quiz.hints_enabled ? 'success' : 'default'}
                                variant="filled"
                                onClick={handleHintToggle}
                                disabled={hintToggleLoading}
                                style={{
                                    cursor: 'pointer',
                                    color: quiz.hints_enabled ? '#fff' : '#333',
                                    backgroundColor: quiz.hints_enabled ? '#2e7d32' : '#e0e0e0',
                                    border: 'none',
                                    fontWeight: 600
                                }}
                            />
                        </Tooltip>
                    )}
                    
                    {/* Browser Lock Badge */}
                    {quiz && (
                        <Tooltip 
                            title={quiz.browser_lock 
                                ? "Students cannot leave the session page until completion"
                                : "Students can freely navigate away from the session page"
                            }
                            arrow
                        >
                            <Chip
                                label={`Browser Lock ${quiz.browser_lock ? 'Enabled' : 'Disabled'}`}
                                color={quiz.browser_lock ? 'warning' : 'default'}
                                variant="filled"
                                onClick={handleBrowserLockToggle}
                                disabled={browserLockToggleLoading}
                                style={{
                                    cursor: 'pointer',
                                    color: quiz.browser_lock ? '#fff' : '#333',
                                    backgroundColor: quiz.browser_lock ? '#ed6c02' : '#e0e0e0',
                                    border: 'none',
                                    fontWeight: 600
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
                
                {/* Error Message */}
                {hintToggleError && (
                    <div style={{
                        backgroundColor: '#ffebee',
                        border: '1px solid #f44336',
                        borderRadius: '6px',
                        padding: '12px',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}>
                        <Typography variant="body2" color="error" style={{ marginBottom: '8px' }}>
                            {hintToggleError}
                        </Typography>
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleTerminateAllSessions}
                            disabled={terminateSessionsLoading}
                            style={{ 
                                color: '#1976d2',
                                textTransform: 'none',
                                padding: '4px 8px',
                                minWidth: 'auto'
                            }}
                        >
                            {terminateSessionsLoading ? 'Terminating...' : 'Terminate all sessions'}
                        </Button>
                    </div>
                )}
                
                {/* Browser Lock Error Message */}
                {browserLockToggleError && (
                    <div style={{
                        backgroundColor: '#fff3e0',
                        border: '1px solid #ff9800',
                        borderRadius: '6px',
                        padding: '12px',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}>
                        <Typography variant="body2" color="error" style={{ marginBottom: '8px' }}>
                            {browserLockToggleError}
                        </Typography>
                    </div>
                )}
                
                {questions.map((question) => (
                    <QuestionCard
                        key={question.id}
                        question={question}
                        handleDelete={handleDelete}
                        handleUpdate={handleUpdate}
                    />
                ))}

            <div className="review-actions-container">
                <div className="top-row-buttons">
                    <Button
                        className="secondary-button"
                        onClick={() => {navigate(`/quiz/${quizId}/add-question`)}}
                    >
                        Add Question
                    </Button>
                    <Button
                        className="secondary-button"
                        onClick={() => setUpdateGradesModalOpen(true)}
                    >
                        Update Grades
                    </Button>
                </div>
                <div className="submit-button-container">
                    <Button
                        className="primary-button submit-button"
                         onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </div>
            </div>

            </div>
            <UpdateQuizGradesModal
                open={updateGradesModalOpen}
                onClose={handleModalClose}
                questions={questions}
                quizId={quizId}
            />
        </>
    );
}

export default QuizReviewPage;
